import { checkMeasurementCookieConsent, getLanguageFromLocalStorage } from "utils";

import {
  SOILSIGHT_FLOW_VERSION,
  SOILSIGHT_LANDING_STEP,
  SOILSIGHT_ROUTE,
  SOILSIGHT_STEPS,
  SOILSIGHT_STEP_ROUTE,
  SOILSIGHT_TRACKING_EVENTS,
  SoilSightStep,
  type SoilSightTrackingStep,
} from "consts";

// Views re-fire on their own once consent lands, so queuing them would double-count. Of the remaining
// discrete actions, SIGNUP_SUBMIT/SIGNUP_COMPLETE/LOGIN_SUBMIT are deliberately left out too: they fire
// as the user leaves this flow (or carry PII), so a queued copy would never get flushed or shouldn't be held.
const QUEUEABLE_EVENTS: string[] = [
  SOILSIGHT_TRACKING_EVENTS.FLOW_START,
  SOILSIGHT_TRACKING_EVENTS.STEP_COMPLETE,
  SOILSIGHT_TRACKING_EVENTS.SIGNUP_CLICK,
  SOILSIGHT_TRACKING_EVENTS.LOGIN_CLICK,
];

const FLOW_START_SESSION_KEY = SOILSIGHT_TRACKING_EVENTS.FLOW_START;
const PENDING_EVENTS_SESSION_KEY = "soilsight_pending_events";
// A full run of the flow emits far fewer than this ... the cap only stops a broken consent state growing forever.
const MAX_PENDING_EVENTS = 20;

const TRACKING_PAGE: Record<SoilSightTrackingStep, { path: string; title: string }> = {
  [SOILSIGHT_LANDING_STEP]: { path: SOILSIGHT_ROUTE, title: "SoilSight" },
  location: { path: SOILSIGHT_STEP_ROUTE("location"), title: "SoilSight Location" },
  rotation: { path: SOILSIGHT_STEP_ROUTE("rotation"), title: "SoilSight Rotation" },
  forecast: { path: SOILSIGHT_STEP_ROUTE("forecast"), title: "SoilSight Forecast" },
  signup: { path: SOILSIGHT_STEP_ROUTE("signup"), title: "SoilSight Signup" },
};

type DataLayerWindow = Window & { dataLayer?: Record<string, unknown>[] };

// GTM's own snippet does `dataLayer = dataLayer || []` and replays whatever it finds, so seeding the array
// before gtm.js arrives loses nothing: GTM is built to adopt a pre-existing queue.
const getDataLayer = () => {
  const target = window as DataLayerWindow;
  target.dataLayer = Array.isArray(target.dataLayer) ? target.dataLayer : [];
  return target.dataLayer;
};

// Private browsing modes throw on session storage access, and losing tracking beats breaking the flow.
const readSessionValue = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSessionValue = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // ignored on purpose, see readSessionValue
  }
};

const readPendingEvents = (): Record<string, unknown>[] => {
  try {
    const stored = JSON.parse(readSessionValue(PENDING_EVENTS_SESSION_KEY) ?? "[]");
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

const queuePendingEvent = (payload: Record<string, unknown>) => {
  const queued = [...readPendingEvents(), payload];
  // The session guard stops flow_start from being re-queued, so the cap must never evict it.
  const flowStart = queued.filter(({ event }) => event === SOILSIGHT_TRACKING_EVENTS.FLOW_START);
  const rest = queued.filter(({ event }) => event !== SOILSIGHT_TRACKING_EVENTS.FLOW_START);

  writeSessionValue(PENDING_EVENTS_SESSION_KEY, JSON.stringify([...flowStart, ...rest.slice(flowStart.length - MAX_PENDING_EVENTS)]));
};

// Iubenda reloads the page the moment consent is granted, and GTM only loads afterwards, so the queue has
// to outlive the JS heap. Session storage does; an in-memory buffer would be wiped by that reload.
export const flushPendingSoilSightEvents = () => {
  if (!checkMeasurementCookieConsent()) return;

  const pending = readPendingEvents();
  if (!pending.length) return;

  writeSessionValue(PENDING_EVENTS_SESSION_KEY, "[]");
  const dataLayer = getDataLayer();
  pending.forEach((payload) => dataLayer.push(payload));
};

export const stepFromPath = (path: string): SoilSightTrackingStep =>
  SOILSIGHT_STEPS.find((step) => path === SOILSIGHT_STEP_ROUTE(step)) ?? SOILSIGHT_LANDING_STEP;

const pushSoilSightEvent = (event: string, params: Record<string, unknown>) => {
  const payload = {
    event,
    flow_version: SOILSIGHT_FLOW_VERSION,
    locale: getLanguageFromLocalStorage(),
    ...params,
  };

  // Measurement consent only: requiring every purpose would hide anyone who accepts analytics but declines marketing.
  if (!checkMeasurementCookieConsent()) {
    if (QUEUEABLE_EVENTS.includes(event)) queuePendingEvent(payload);
    return false;
  }

  // Keep replayed events ahead of the current one so the dataLayer stays in chronological order.
  flushPendingSoilSightEvents();
  getDataLayer().push(payload);
  return true;
};

export const trackSoilSightVirtualPageView = (step: SoilSightTrackingStep) => {
  const { path, title } = TRACKING_PAGE[step];
  pushSoilSightEvent(SOILSIGHT_TRACKING_EVENTS.VIRTUAL_PAGE_VIEW, {
    step,
    page_location: `${window.location.origin}${path}`,
    page_title: title,
  });
};

export const trackSoilSightFlowStart = () => {
  if (readSessionValue(FLOW_START_SESSION_KEY)) return;
  // Marked before the push: a queued flow start is still going to arrive, so it must not be queued twice.
  writeSessionValue(FLOW_START_SESSION_KEY, "1");
  pushSoilSightEvent(SOILSIGHT_TRACKING_EVENTS.FLOW_START, { step: SOILSIGHT_LANDING_STEP });
};

export const trackSoilSightStepView = (step: SoilSightStep) => {
  pushSoilSightEvent(SOILSIGHT_TRACKING_EVENTS.STEP_VIEW, { step });
};

export const trackSoilSightStepComplete = (step: SoilSightStep) => {
  pushSoilSightEvent(SOILSIGHT_TRACKING_EVENTS.STEP_COMPLETE, { step });
};

export const trackSoilSightSignupClick = (step: SoilSightTrackingStep) => {
  pushSoilSightEvent(SOILSIGHT_TRACKING_EVENTS.SIGNUP_CLICK, { step });
};

export const trackSoilSightSignupSubmit = (email: string, success: boolean) => {
  pushSoilSightEvent(SOILSIGHT_TRACKING_EVENTS.SIGNUP_SUBMIT, { step: "signup", success, user_data: { email_address: email } });
};

export const trackSoilSightSignupComplete = () => {
  pushSoilSightEvent(SOILSIGHT_TRACKING_EVENTS.SIGNUP_COMPLETE, { step: "signup" });
};

export const trackSoilSightLoginClick = () => {
  pushSoilSightEvent(SOILSIGHT_TRACKING_EVENTS.LOGIN_CLICK, { step: "signup" });
};

export const trackSoilSightLoginSubmit = (success: boolean) => {
  pushSoilSightEvent(SOILSIGHT_TRACKING_EVENTS.LOGIN_SUBMIT, { step: "login", success });
};
